import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import DropdownToggleDemoMode from "@components/store/navigation/demo-mode-dropdown";

import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useGoogleAnalyticsActionEvents from "@hooks/use-analytics-action-events";
import useAnalyticsActionEvents from "@stadium/services/analytics/hooks/use-action-events";
import { CurrentUserContext } from "@contexts/current-user-provider";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { useWorkspaceContext } from "@contexts/workspace-context";

import { isEshopStore } from "@helpers/store";
import { getEventName } from "@stadium/helpers/analytics";

import { getDefaultBuildYourOwnShopNumber } from "@stadium/redux-utils/selectors/configSelector";
import { getStoreNumber, getStoreName, getStoreType } from "@store/selectors/store";

import { externalRoutes } from "@constants/external-routes";
import { DEMO_MODE_STORE_CURRENCY_OPTIONS } from "@constants/store";
import { JUNE_SO } from "@stadium/services/analytics/constants/services";
import { PREVIEW_MODE, USE_TEMPLATE } from "@constants/june-so";
import {
  getNewShopCreationRoute,
  getPreviewTemplateRoute,
  getStoreBasePath,
} from "@constants/routes";

import styles from "./main.module.scss";

const services = [JUNE_SO];

const DemoModeBar = () => {
  const { countryCode, lang, storeCode, workspaceSlug, isNewRoute } = useStoreContext();
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const [demoDropdownOpen, setDemoDropdownOpen] = useState(false);

  const storeType = useSelector(getStoreType);
  const storeName = useSelector(getStoreName);
  const storeNumber = useSelector(getStoreNumber);
  const buildYourOwnShopNumber = useConfig(getDefaultBuildYourOwnShopNumber);

  const { isNewShopCreationFlow, setDummyStoreReturnUrl } = useCreateShopContext();
  const { resetPreviewMode, setPreviewModeCurrency, previewModeCurrency } = usePreviewModeContext();

  const { currentUserData: { company = {} } = {} } = useContext(CurrentUserContext);

  const { trackShopPreview } = useGoogleAnalyticsActionEvents();

  const { trackEvent } = useAnalyticsActionEvents(services);

  useEffect(() => {
    if (storeName && storeNumber) {
      const eventObj = { shop_id: storeNumber, shop_name: storeName };
      trackShopPreview(eventObj);
    }
  }, [storeName, storeNumber]);

  const backToTemplateHandler = () => {
    setTimeout(function () {
      resetPreviewMode(getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.chooseTemplates()));
    }, 500);
  };

  const homePageRoute = getStoreBasePath({
    countryCode,
    lang,
    storeCode,
    storeNumber,
    workspaceSlug,
    isNewShopCreationFlow,
    isNewRoute,
  });

  const getUseTemplateRoute = () => {
    setDummyStoreReturnUrl(getPreviewTemplateRoute(homePageRoute));
    return getNewShopCreationRoute({
      countryCode,
      lang,
      storeCode,
      storeNumber: !isEshopStore(storeType) ? storeNumber : buildYourOwnShopNumber,
      workspaceSlug,
      isNewRoute,
    });
  };

  const handleCtaClick = () => {
    !isEshopStore(storeType) &&
      trackEvent({
        eventName: getEventName({
          pageName: PREVIEW_MODE,
          target: USE_TEMPLATE,
          action: "clicked",
        }),
        eventProperties: {
          shop_name: storeName,
          shop_number: storeNumber,
        },
        context: { groupId: company?.id },
        additionalProps: { companyName: company?.name },
      });
    resetPreviewMode(getUseTemplateRoute());
  };

  const renderDemoModeLinks = () => (
    <>
      <div className={styles.fixedBarRightBlock}>
        <div className={styles.fixedBarCurrencyBlock}>
          {Object.values(DEMO_MODE_STORE_CURRENCY_OPTIONS).map((currency) => {
            const { currencyCode, currencyDisplayName } = currency;
            const isActive = previewModeCurrency?.currencyCode === currencyCode;
            return (
              <button
                className={`${isActive ? styles.active : ""} ${styles.fixedBarCurrencyBtns}`}
                onClick={() => setPreviewModeCurrency(currency)}
                key={currencyCode}
              >
                {currencyDisplayName}
              </button>
            );
          })}
        </div>
      </div>
      <div className={styles.fixedBarRightBlockBtnHolder}>
        <button className={styles.btnCreateStore} onClick={handleCtaClick}>
          {!isEshopStore(storeType) ? "USE TEMPLATE" : "CREATE SHOP"}
        </button>
      </div>
    </>
  );

  const backToTemplatesCta = () => (
    <>
      <button className={styles.btnLink} onClick={backToTemplateHandler}>
        <i className={styles.iconArrowLeft} />
        <span className={styles.backToTemplate}>Back To Templates</span>
      </button>
      <div className={styles.backTemplateDemoMessage}>You're currently in demo mode</div>
    </>
  );

  const renderDesktopDemoModeBar = () => (
    <>
      <div className={`${styles.colAuto} ${styles.fixedBarBackBlock}`}>{backToTemplatesCta()}</div>
      <div className={styles.fixedBarToggleBlock}>
        <DropdownToggleDemoMode
          setDemoDropdownOpen={setDemoDropdownOpen}
          demoDropdownOpen={demoDropdownOpen}
        />
      </div>
      {renderDemoModeLinks()}
    </>
  );

  const renderMobileDemoModeBar = () => (
    <>
      {backToTemplatesCta()}
      <div className={styles.fixedBarToggleBlock}>
        <DropdownToggleDemoMode
          setDemoDropdownOpen={setDemoDropdownOpen}
          demoDropdownOpen={demoDropdownOpen}
        />
        {demoDropdownOpen && renderDemoModeLinks()}
      </div>
    </>
  );

  const openDemoModeBar = () => isMobileDevice && setDemoDropdownOpen(!demoDropdownOpen);

  return (
    <div className={`${styles.storeHeroTopNav} ${demoDropdownOpen ? styles.open : ""}`}>
      <div className={`${styles.navContainer} ${styles.justifyContent}`}>
        {isMobileDevice ? renderMobileDemoModeBar() : renderDesktopDemoModeBar()}
      </div>
      <button className={styles.demoMobileClickable} onClick={openDemoModeBar} />
    </div>
  );
};

export default DemoModeBar;
